import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField as MuiTextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import Parse from 'parse';
import CustomPagination from '../../components/CustomPagination';

const PlayerAccountSearch = () => {
  const navigate = useNavigate();
  // Player ID and Username share a single search box (backend matches either).
  const [searchTerm, setSearchTerm] = useState('');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const getSearchTerm = () => searchTerm.trim();

  const fetchData = async (customPage = 1) => {
    const searchTerm = getSearchTerm();
    if (!searchTerm) return;

    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const operatorId = localStorage.getItem('operatorId');

      const result = await Parse.Cloud.run(
        'getPlayerWiseOperatorCoins',
        {
          page: customPage,
          perPage,
          operatorId,
          field: 'id',
          order: 'DESC',
          filter: { playerUsername: searchTerm }
        },
        { sessionToken }
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error(err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!getSearchTerm()) return;
    setPage(1);
    setHasSearched(true);
    fetchData(1);
  };

  const handleClear = () => {
    setSearchTerm('');
    setData([]);
    setTotal(0);
    setHasSearched(false);
    setPage(1);
  };

  const handleAccounting = (row) => {
    navigate('/player-account/accounting', {
      state: { playerId: row.id, playerUsername: row.playerUsername }
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Filter Card */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, mb: 2, fontSize: 15 }}>
          Search Player
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
          <MuiTextField
            size="small"
            label="Player ID / Username"
            placeholder="Enter Player ID or Username"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ ...fieldSx, minWidth: 280 }}
          />

          <Button
            variant="contained"
            startIcon={
              loading ? <CircularProgress size={16} sx={{ color: '#000' }} /> : <SearchIcon />
            }
            onClick={handleSearch}
            disabled={loading || !getSearchTerm()}
            sx={applyBtnSx}
          >
            Search
          </Button>

          <Button variant="outlined" onClick={handleClear} disabled={loading} sx={clearBtnSx}>
            Clear
          </Button>
        </Box>
      </Box>

      {/* Results Table */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2 }}>
        <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#2A2A2A' }}>
                {['Player ID', 'Nickname', 'Current Balance', 'Action'].map((col) => (
                  <TableCell key={col} sx={{ color: '#FFC107', fontWeight: 600 }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: '#FFC107' }} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#fff' }}>
                    {hasSearched
                      ? 'No players found matching the search criteria.'
                      : 'Enter a search value and click Search to find player accounts.'}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#2A2A2A' } }}>
                    <TableCell sx={{ color: '#fff' }}>{row.id}</TableCell>
                    <TableCell sx={{ color: '#FFC107', fontWeight: 500 }}>
                      {row.nickname || row.username || row.playerUsername}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      {row.currentBalance != null ? Number(row.currentBalance).toFixed(2) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Accounting"
                        size="small"
                        onClick={() => handleAccounting(row)}
                        sx={{
                          backgroundColor: 'rgba(255,193,7,0.15)',
                          color: '#FFC107',
                          fontWeight: 600,
                          cursor: 'pointer',
                          border: '1px solid rgba(255,193,7,0.4)',
                          '&:hover': { backgroundColor: 'rgba(255,193,7,0.25)' }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 2 }}>
            <CustomPagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={(p) => {
                setPage(p);
                fetchData(p);
              }}
              setPerPage={(pp) => {
                setPerPage(pp);
                setPage(1);
                fetchData(1);
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FFC107'
  }
};

const applyBtnSx = {
  background: '#FFC107',
  color: '#0A0A0A',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#FFA000' },
  '&:disabled': { backgroundColor: 'rgba(255,193,7,0.3)', color: 'rgba(0,0,0,0.4)' }
};

const clearBtnSx = {
  borderColor: '#FFC107',
  color: '#FFC107',
  textTransform: 'none',
  '&:hover': { borderColor: '#FFA000', color: '#FFA000', backgroundColor: 'rgba(255,193,7,0.08)' }
};

export default PlayerAccountSearch;
